import React, { useEffect, useState, useRef } from "react";
import "../../../../../css/Recruitment/Sections.css";
import YesNoRadio from "../RadioButton";
import { writeDataS6 } from "../../../../../backend/Recruitment/Sections/Section6/writeDataS6";
import { readDataS6 } from "../../../../../backend/Recruitment/Sections/Section6/readDataS6";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import UseDatepicker from "../Datepicker";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";

import { validateSection6, isAnyYesSelectedS6 } from "../Validation/section6"; //adjust path if needed
import InputField from "../../../../DesignSystem/Inputs/InputField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import FileUpload from "../../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../../DesignSystem/Upload/UploadedFileList";
import { formatDateValue } from "../Utils/dateFormatting";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import TextArea from "../../../../DesignSystem/Inputs/TextArea";

const toArray = (v, fallback = []) =>
  Array.isArray(v)
    ? v
    : v && typeof v === "object"
      ? Object.values(v)
      : fallback;

const safeDate = (v) => {
  if (!v) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object" && v?.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d;
};

const Section6 = ({ onValidationStatusChange }) => {
  const sectionTopRef = useRef(null);
  const [formData, setFormData] = useState({
    dismissed: "",
    receivedWarnings: "",
    investigationOrReview: "",
    additionalTraining: "",
    assessedByFCA: "",
    assessmentReport: "",
    removedFromLendersPanel: "",
    actedInDirectlyAuthorisedCapacity: "",
    latestRMAR: "",
    treatedCustomersFairly: "",
    highRiskProducts: "",
    complaint: "",
    subjectOfBreach: "",
    appliedToRosemount: "",
    tradingNames: "",
    tradingName: "",
    shareholder: "",
    currentContracts: "",
    directorOfFinancialServicesCompany: "",
    outstandingDebts: "",
    currentDPL: "",
    referenceNumber: "",
    additionalInformation: "",
    files: [],
    additionalInformationS6_1: "",
    additionalInformationS6_3: "",
    additionalInformationDBPensionTransfers: "",
    advisedOnDBPensionTransfers: "",
    referredClientToAnotherFirm: "",
    advisedOnUnregulatedInvestment: "",
    additionalInformationReferredClientToAnotherFirm: "",
    filesS6_1: [],
    filesS6_3: [],
    complaints: [
      {
        customer: "",
        complaintDate: "",
        natureOfComplaint: "",
        outcome: "",
        redressCompensationAmount: "",
      },
    ],
    director: [{ companyName: "", businessRelationship: "" }],
    debtDetails: [{ company: "", amountOfDebt: "", detailsOfArrangements: "" }],
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());

  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => removeListener();
  }, []);

  // single source of truth validation
  const runValidation = (nextData) => {
    const v = validateSection6(nextData);
    const { _isValid, ...fieldErrors } = v;

    setErrors(fieldErrors);
    setIsValid(!!_isValid);

    errorTrackerV2.setSectionErrors("section6", fieldErrors);
    onValidationStatusChange?.(!!_isValid);

    return !!_isValid;
  };

  // load data
  useEffect(() => {
    const fetchData = async () => {
      const data = await readDataS6();
      if (data) {
        setFormData((prev) => ({
          ...prev,
          ...data,
          complaints: toArray(data.complaints, []).map((c) => ({
            ...c,
            complaintDate: safeDate(c.complaintDate),
          })),
          director: toArray(data.director, [
            { companyName: "", businessRelationship: "" },
          ]),
          debtDetails: toArray(data.debtDetails, [
            { company: "", amountOfDebt: "", detailsOfArrangements: "" },
          ]),
          additionalInformation: data.additionalInformation || "",
          files: data.files || [],
          filesS6_1: data.filesS6_1 || [],
          filesS6_3: data.filesS6_3 || [],
        }));
      }
    };

    fetchData();
  }, []);

  // revalidate on change
  useEffect(() => {
    runValidation(formData);
  }, [formData]);

  // rows
  const handleAddRow = (sectionName) => {
    const newRow =
      sectionName === "complaints"
        ? {
            customer: "",
            complaintDate: "",
            natureOfComplaint: "",
            outcome: "",
            redressCompensationAmount: "",
          }
        : sectionName === "director"
          ? { companyName: "", businessRelationship: "" }
          : { company: "", amountOfDebt: "", detailsOfArrangements: "" };

    setFormData((prev) => ({
      ...prev,
      [sectionName]: [...(prev[sectionName] || []), newRow],
    }));
  };

  const handleRemoveRow = (sectionName) => {
    setFormData((prev) => {
      const arr = Array.isArray(prev[sectionName]) ? prev[sectionName] : [];
      if (arr.length <= 1) return prev;
      return { ...prev, [sectionName]: arr.slice(0, -1) };
    });
  };

  const handleChange = (e, sectionName = null, index = null) => {
    const { name, value } = e.target;

    if (sectionName && index !== null) {
      setFormData((prev) => {
        const updatedArray = [...(prev[sectionName] || [])];
        updatedArray[index] = { ...(updatedArray[index] || {}), [name]: value };
        return { ...prev, [sectionName]: updatedArray };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // drag/drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, sectionFiles) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    setFormData((prev) => ({
      ...prev,
      [sectionFiles]: [...(prev[sectionFiles] || []), ...files],
    }));
  };

  const handleFileChange = (e, sectionFiles) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, [sectionFiles]: files }));
  };

  const handleRemoveFile = (indexToRemove, sectionFiles) => {
    setFormData((prev) => ({
      ...prev,
      [sectionFiles]: (prev[sectionFiles] || []).filter(
        (_, i) => i !== indexToRemove,
      ),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    const ok = runValidation(formData);

    // Save data regardless of validation status
    writeDataS6(formData);

    // Show appropriate alert based on validation result
    if (ok) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const anyYes = isAnyYesSelectedS6(formData);

  // Check if any yes in 6.1 (General)
  const anyYesS6_1 = [
    formData.dismissed,
    formData.receivedWarnings,
    formData.investigationOrReview,
    formData.additionalTraining,
    formData.assessedByFCA,
    formData.assessmentReport,
    formData.removedFromLendersPanel,
    formData.actedInDirectlyAuthorisedCapacity,
    formData.latestRMAR,
    formData.treatedCustomersFairly,
    formData.highRiskProducts,
    formData.complaint,
    formData.subjectOfBreach,
    formData.appliedToRosemount,
  ].some((val) => val === true || val === "true");

  // Check if any yes in 6.3 (Other)
  const anyYesS6_3 = [
    formData.tradingNames,
    formData.shareholder,
    formData.currentContracts,
    formData.directorOfFinancialServicesCompany,
    formData.outstandingDebts,
  ].some((val) => val === true || val === "true");

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 6 - Business Conduct</h3>

      <div className="Content">
        Non-disclosure of relevant information may result in your application
        being declined.
        <br />
        If you answer Yes to any of these questions, please provide full details
        in the text box.
      </div>
      <div className="Content">
        6.1 General
        <span style={{ color: "var(--Error)" }}> *</span>
      </div>

      {/* a */}
      <div className="input-group-row">
        <div className="Content">
          a) Have you ever been dismissed or asked to leave by any employer or
          network of life Assurance company?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="dismissed"
            value={formData.dismissed}
            onChange={handleChange}
            error={showErrors && errors.dismissed}
          />
        </div>
      </div>

      {/* b */}
      <div className="input-group-row">
        <div className="Content">
          b) Have you ever received any warnings, written or verbal?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="receivedWarnings"
            value={formData.receivedWarnings}
            onChange={handleChange}
            error={showErrors && errors.receivedWarnings}
          />
        </div>
      </div>

      {/* c */}
      <div className="input-group-row">
        <div className="Content">
          c) Have you ever had an investigation or review carried out on you or
          the business conducted by you?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="investigationOrReview"
            value={formData.investigationOrReview}
            onChange={handleChange}
            error={showErrors && errors.investigationOrReview}
          />
        </div>
      </div>

      {/* d */}
      <div className="input-group-row">
        <div className="Content">
          d) Have you ever undertaken or been asked to undertake any additional
          training, including increased supervision, under any Training and
          Competence Scheme? <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="additionalTraining"
            value={formData.additionalTraining}
            onChange={handleChange}
            error={showErrors && errors.additionalTraining}
          />
        </div>
      </div>

      {/* e */}
      <div className="input-group-row">
        <div className="Content">
          e) Have you ever been assessed by the FCA or any authorised firm or
          regulatory body? <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="assessedByFCA"
            value={formData.assessedByFCA}
            onChange={handleChange}
            error={showErrors && errors.assessedByFCA}
          />
        </div>
      </div>

      {/* f */}
      <div className="input-group-row">
        <div className="Content">
          f) If you have been assessed please provide a copy of your assessment
          report? and RMAR (if applicable)?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            na
            name="assessmentReport"
            value={formData.assessmentReport}
            onChange={handleChange}
            error={showErrors && errors.assessmentReport}
          />
        </div>
      </div>

      {/* g */}
      <div className="input-group-row">
        <div className="Content">
          g) Have you ever been removed from a Lenders panel or had an agency
          refused or terminated by a Product Provider?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="removedFromLendersPanel"
            value={formData.removedFromLendersPanel}
            onChange={handleChange}
            error={showErrors && errors.removedFromLendersPanel}
          />
        </div>
      </div>

      {/* h */}
      <div className="input-group-row">
        <div className="Content">
          h) Have you ever acted in a Directly Authorised capacity?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="actedInDirectlyAuthorisedCapacity"
            value={formData.actedInDirectlyAuthorisedCapacity}
            onChange={handleChange}
            error={showErrors && errors.actedInDirectlyAuthorisedCapacity}
          />
        </div>
      </div>

      {/* i */}
      <div className="input-group-row">
        <div className="Content">
          i) If yes, please provide a copy of your latest RMAR. Is this
          enclosed? <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="latestRMAR"
            value={formData.latestRMAR}
            onChange={handleChange}
            error={showErrors && errors.latestRMAR}
            na={true}
          />
        </div>
      </div>

      {/* j */}
      <div className="input-group-row">
        <div className="Content">
          j) Have you ever been subject to any remedial or corrective action in
          relation to Treating Customers Fairly or Consumer Duty?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="treatedCustomersFairly"
            value={formData.treatedCustomersFairly}
            onChange={handleChange}
            error={showErrors && errors.treatedCustomersFairly}
          />
        </div>
      </div>

      {/* k */}
      <div className="input-group-row">
        <div className="Content">
          k) Have you ever been involved in the promotion, advice or selling of
          Arch Cru, British Steel, Key Data or high risk products?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="highRiskProducts"
            value={formData.highRiskProducts}
            onChange={handleChange}
            error={showErrors && errors.highRiskProducts}
          />
        </div>
      </div>

      {/* l */}
      <div className="input-group-row">
        <div className="Content">
          l) Have you ever had any complaints against you whether upheld or not?
          If yes please complete section 6.2 below.{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="complaint"
            value={formData.complaint}
            onChange={handleChange}
            error={showErrors && errors.complaint}
          />
        </div>
      </div>

      {/* m */}
      <div className="input-group-row">
        <div className="Content">
          m) Have you ever been the subject of a breach in relation to
          activities regulated by the Financial Conduct Authority.{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="subjectOfBreach"
            value={formData.subjectOfBreach}
            onChange={handleChange}
            error={showErrors && errors.subjectOfBreach}
          />
        </div>
      </div>

      {/* n */}
      <div className="input-group-row">
        <div className="Content">
          n) Have you ever applied to Rosemount prior to this application?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="appliedToRosemount"
            value={formData.appliedToRosemount}
            onChange={handleChange}
            error={showErrors && errors.appliedToRosemount}
          />
        </div>
      </div>

      {/* o*/}
      <div>
        <div className="input-group-row">
          <div className="Content">
            o) Have you ever advised on Defined Benefit (DB) pension transfers?{" "}
            <span style={{ color: "var(--Error)" }}> *</span>
          </div>
          <div className="input-group">
            <YesNoRadio
              name="advisedOnDBPensionTransfers"
              value={formData.advisedOnDBPensionTransfers}
              onChange={handleChange}
              error={showErrors && errors.advisedOnDBPensionTransfers}
            />
          </div>
        </div>
        {formData.advisedOnDBPensionTransfers === true && (
          <div>
            <div className="Content">
              Please provide us further information, including number of cases &
              whether you have had any complaints in relation to the DB
              business.<span style={{ color: "var(--Error)" }}> *</span>
            </div>
            <TextArea
              className="long-textarea"
              style={{ width: "100%" }}
              multiline
              error={
                showErrors &&
                formData.advisedOnDBPensionTransfers === true &&
                (
                  formData.additionalInformationDBPensionTransfers || ""
                ).trim() === ""
                  ? "This field is required"
                  : false
              }
              rows={4}
              placeholder="Enter your text here..."
              name="additionalInformationDBPensionTransfers"
              value={formData.additionalInformationDBPensionTransfers}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  additionalInformationDBPensionTransfers: e.target.value,
                }))
              }
            />
          </div>
        )}{" "}
      </div>
      {/* p */}
      <div>
        <div className="input-group-row">
          <div className="Content">
            p) Have you ever referred a client to another firm or adviser in
            relation to a Defined Benefit (DB) pension transfers?
            <span style={{ color: "var(--Error)" }}> *</span>
          </div>
          <div className="input-group">
            <YesNoRadio
              name="referredClientToAnotherFirm"
              value={formData.referredClientToAnotherFirm}
              onChange={handleChange}
              error={showErrors && errors.referredClientToAnotherFirm}
            />
          </div>
        </div>
        {formData.referredClientToAnotherFirm === true && (
          <div>
            <div className="Content">
              Please provide us further information, including number of cases &
              who you referred them to.
              <span style={{ color: "var(--Error)" }}> *</span>
            </div>
            <TextArea
              className="long-textarea"
              style={{ width: "100%" }}
              multiline
              rows={4}
              placeholder="Enter your text here..."
              name="additionalInformationReferredClientToAnotherFirm"
              value={formData.additionalInformationReferredClientToAnotherFirm}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  additionalInformationReferredClientToAnotherFirm:
                    e.target.value,
                }))
              }
              error={
                showErrors &&
                formData.referredClientToAnotherFirm === true &&
                (
                  formData.additionalInformationReferredClientToAnotherFirm ||
                  ""
                ).trim() === ""
                  ? "This field is required"
                  : false
              }
            />
          </div>
        )}{" "}
      </div>
      <div className="input-group-row">
        <div className="Content">
          q) Have you ever advised on, or referred business that resulted in, a
          client investing in an unregulated investment?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="advisedOnUnregulatedInvestment"
            value={formData.advisedOnUnregulatedInvestment}
            onChange={handleChange}
            error={showErrors && errors.referredClientToAnotherFirm}
          />
        </div>
      </div>
      {anyYesS6_1 && (
        <div>
          <div className="Content">Please provide full details below:</div>
          <TextArea
            className="long-textarea"
            style={{ width: "100%" }}
            multiline
            rows={4}
            placeholder="Enter your text here..."
            name="additionalInformationS6_1"
            value={formData.additionalInformationS6_1}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                additionalInformationS6_1: e.target.value,
              }))
            }
            error={
              anyYesS6_1 &&
              (formData.additionalInformationS6_1 || "").trim() === ""
                ? "This field is required"
                : false
            }
            helperText={
              showErrors &&
              anyYesS6_1 &&
              (formData.additionalInformationS6_1 || "").trim() === ""
                ? "This field is required"
                : ""
            }
            showError={showErrors}
          />
          <div className="sizebox" />

          <FileUpload
            // label="Upload Files for Section 6.1"
            description="Drag & drop files here or"
            browseText="Browse"
            onDrop={(e) => handleDrop(e, "filesS6_1")}
            onBrowse={() => document.getElementById("fileInputS6_1").click()}
          />
          <input
            type="file"
            multiple
            onChange={(e) => handleFileChange(e, "filesS6_1")}
            style={{ display: "none" }}
            id="fileInputS6_1"
          />

          {formData.filesS6_1.length > 0 && (
            <UploadedFileList
              files={formData.filesS6_1}
              onRemove={(file, index) =>
                handleRemoveFile(formData.filesS6_1.indexOf(file), "filesS6_1")
              }
            />
          )}

          {/* <div className="separator" /> */}
        </div>
      )}

      <div className="separator" />
      <div className="Content">
        6.2 Complaints <br />
        Please provide full details regarding any complaints against you whether
        upheld or not.
      </div>

      <div className="complaint-table-container">
        <table className="complaint-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Date</th>
              <th>Nature of the complaint</th>
              <th>Outcome/Settlement</th>
              <th>Redress compensation amount</th>
            </tr>
          </thead>
          <tbody>
            {formData.complaints.map((complaint, index) => (
              <tr key={index}>
                <td>
                  <InputField
                    name="customer"
                    value={complaint.customer}
                    error={showErrors && errors[`customer_${index}`]}
                    onChange={(e) => handleChange(e, "complaints", index)}
                  />
                </td>
                <td>
                  <DateField
                    name="complaintDate"
                    value={formatDateValue(complaint.complaintDate)}
                    error={showErrors && !!errors[`complaintDate_${index}`]}
                    helperText={
                      showErrors ? errors[`complaintDate_${index}`] : ""
                    }
                    onChange={(e) => {
                      const dateValue = e.target.value
                        ? new Date(e.target.value)
                        : "";
                      handleChange(
                        { target: { name: "complaintDate", value: dateValue } },
                        "complaints",
                        index,
                      );
                    }}
                  />
                </td>
                <td>
                  <InputField
                    name="natureOfComplaint"
                    value={complaint.natureOfComplaint}
                    error={showErrors && errors[`natureOfComplaint_${index}`]}
                    onChange={(e) => handleChange(e, "complaints", index)}
                  />
                </td>
                <td>
                  <InputField
                    name="outcome"
                    value={complaint.outcome}
                    error={showErrors && errors[`outcome_${index}`]}
                    onChange={(e) => handleChange(e, "complaints", index)}
                  />
                </td>
                <td>
                  <InputField
                    type="number"
                    name="redressCompensationAmount"
                    value={complaint.redressCompensationAmount}
                    error={
                      showErrors && errors[`redressCompensationAmount_${index}`]
                    }
                    onChange={(e) => handleChange(e, "complaints", index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="button-group">
        <button
          type="button"
          className="icon-button add-button"
          onClick={() => handleAddRow("complaints")}
        >
          <i className="fas fa-plus"></i>
        </button>
        <button
          type="button"
          className="icon-button delete-button"
          onClick={() => handleRemoveRow("complaints")}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>

      <div className="separator" />
      <div className="Content">6.3 Other</div>
      <div className="input-group-row">
        <div className="Content">
          a) Do you have any trading names?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="tradingNames"
            value={formData.tradingNames}
            onChange={handleChange}
            error={showErrors && errors.tradingNames}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          If yes, please give full name (s){" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <InputField
            name="tradingName"
            value={formData.tradingName}
            placeholder="Trading Name"
            onChange={handleChange}
            error={showErrors && errors.tradingName}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          b) Are you a shareholder in any other financial services company where
          your shareholding exceeds 10% of total shares issued?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="shareholder"
            value={formData.shareholder}
            onChange={handleChange}
            error={showErrors && errors.shareholder}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          c) Have you ever held, or do you currently hold, any contracts with
          any financial services company which are not listed in the employment
          section of the application?{" "}
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="currentContracts"
            value={formData.currentContracts}
            onChange={handleChange}
            error={showErrors && errors.currentContracts}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          d) Have you ever been a director of a financial services company?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="directorOfFinancialServicesCompany"
            value={formData.directorOfFinancialServicesCompany}
            onChange={handleChange}
            error={showErrors && errors.directorOfFinancialServicesCompany}
          />
        </div>
      </div>
      <table className="complaint-table">
        <thead>
          <tr>
            <th>Name of Company/Individual</th>
            <th>Details of Business Relationship</th>
          </tr>
        </thead>
        <tbody>
          {formData.director.map((director, index) => (
            <tr key={index}>
              <td>
                <InputField
                  name="companyName"
                  value={director.companyName}
                  error={showErrors && errors[`companyName_${index}`]}
                  onChange={(e) => handleChange(e, "director", index)}
                />
              </td>
              <td>
                <InputField
                  name="businessRelationship"
                  value={director.businessRelationship}
                  error={showErrors && errors[`businessRelationship_${index}`]}
                  onChange={(e) => handleChange(e, "director", index)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="button-group">
        <button
          type="button"
          className="icon-button add-button"
          onClick={() => handleAddRow("director")}
        >
          <i className="fas fa-plus"></i>
        </button>
        <button
          type="button"
          className="icon-button delete-button"
          onClick={() => handleRemoveRow("director")}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>

      <div className="input-group-row">
        <div className="Content">
          e) Do you have any outstanding debts with any Financial Services
          Company? If yes, please enter full details below
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="outstandingDebts"
            value={formData.outstandingDebts}
            onChange={handleChange}
            error={showErrors && errors.outstandingDebts}
          />
        </div>
      </div>
      <table className="complaint-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Amount of Debt</th>
            <th>Details of Arrangements to Repay the Debt</th>
          </tr>
        </thead>
        <tbody>
          {formData.debtDetails.map((debt, index) => (
            <tr key={index}>
              <td>
                <InputField
                  name="company"
                  value={debt.company}
                  error={showErrors && errors[`company_${index}`]}
                  onChange={(e) => handleChange(e, "debtDetails", index)}
                />
              </td>
              <td>
                <InputField
                  type="number"
                  name="amountOfDebt"
                  value={debt.amountOfDebt}
                  error={showErrors && errors[`amountOfDebt_${index}`]}
                  onChange={(e) => handleChange(e, "debtDetails", index)}
                />
              </td>
              <td>
                <InputField
                  name="detailsOfArrangements"
                  value={debt.detailsOfArrangements}
                  error={showErrors && errors[`detailsOfArrangements_${index}`]}
                  onChange={(e) => handleChange(e, "debtDetails", index)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="button-group">
        <button
          type="button"
          className="icon-button add-button"
          onClick={() => handleAddRow("debtDetails")}
        >
          <i className="fas fa-plus"></i>
        </button>
        <button
          type="button"
          className="icon-button delete-button"
          onClick={() => handleRemoveRow("debtDetails")}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>

      {/* Additional information sections moved to 6.1 and 6.3 specific areas */}

      {anyYesS6_3 && (
        <>
          <div className="Content">Please provide full details below:</div>

          <TextArea
            className="long-textarea"
            style={{ width: "100%" }}
            multiline
            rows={4}
            placeholder="Enter your text here..."
            name="additionalInformationS6_3"
            value={formData.additionalInformationS6_3}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                additionalInformationS6_3: e.target.value,
              }))
            }
            error={
              anyYesS6_3 &&
              (formData.additionalInformationS6_3 || "").trim() === ""
                ? "This field is required"
                : false
            }
            helperText={
              showErrors &&
              anyYesS6_3 &&
              (formData.additionalInformationS6_3 || "").trim() === ""
                ? "This field is required"
                : ""
            }
            showError={showErrors}
          />

          <FileUpload
            // label="Upload Files for Section 6.3"
            description="Drag & drop files here or"
            browseText="Browse"
            onDrop={(e) => handleDrop(e, "filesS6_3")}
            onBrowse={() => document.getElementById("fileInputS6_3").click()}
          />
          <input
            type="file"
            multiple
            onChange={(e) => handleFileChange(e, "filesS6_3")}
            style={{ display: "none" }}
            id="fileInputS6_3"
          />

          {formData.filesS6_3.length > 0 && (
            <UploadedFileList
              files={formData.filesS6_3}
              onRemove={(file, index) =>
                handleRemoveFile(formData.filesS6_3.indexOf(file), "filesS6_3")
              }
            />
          )}

          <div className="separator" />
        </>
      )}

      <div className="Content">6.4 Data Protection Licence (DPL)</div>
      <div className="input-group-row">
        <div className="Content">
          Do you have a current DPL?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="currentDPL"
            value={formData.currentDPL}
            onChange={handleChange}
            error={showErrors && errors.currentDPL}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          If yes, please provide a copy or reference number
        </div>
        <div className="input-group">
          <InputField
            name="referenceNumber"
            value={formData.referenceNumber}
            placeholder="Reference Number"
            onChange={handleChange}
            error={showErrors && errors.referenceNumber}
          />
        </div>
      </div>

      <div className="Content">
        If no, please be aware that you must obtain your Data Protection Licence
        from the Information Commissions Office (ICO) prior to representing
        Rosemount Financial Solutions (IFA) Ltd.
      </div>

      {(formData.additionalInformation || formData.files?.length > 0) && (
        <>
          <div className="separator" />
          <div className="Content">Additional Information & Files</div>
          {formData.additionalInformation && (
            <div>
              <div className="Content" style={{ marginTop: "10px" }}>
                Additional Information (Legacy):
              </div>
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  marginTop: "5px",
                }}
              >
                {formData.additionalInformation}
              </div>
            </div>
          )}
          {formData.files && formData.files.length > 0 && (
            <div style={{ marginTop: "15px" }}>
              <div className="Content">Files (Legacy):</div>
              <ul style={{ marginTop: "10px" }}>
                {formData.files.map((file, index) => (
                  <li key={index}>
                    {file.url ? (
                      <a
                        href={file.url}
                        title={file.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.name}
                      </a>
                    ) : (
                      <span>{file.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section6;
